import React from 'react';
import { Button, Box, Typography, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Floating particles */}
      {['🔍', '📄', '❓', '🧭', '📍', '🗂️'].map((emoji, index) => (
        <Box
          key={index}
          sx={{
            position: 'fixed',
            fontSize: '2rem',
            opacity: 0.6,
            animation: 'float 15s infinite linear',
            animationDelay: `${index * 2}s`,
            left: `${10 + index * 10}%`,
            '@keyframes float': {
              '0%': {
                transform: 'translateY(100vh) rotate(0deg)',
                opacity: 0,
              },
              '10%': {
                opacity: 0.6,
              },
              '90%': {
                opacity: 0.6,
              },
              '100%': {
                transform: 'translateY(-100vh) rotate(360deg)',
                opacity: 0,
              },
            },
          }}
        >
          {emoji}
        </Box>
      ))}

      <Container maxWidth="sm">
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            padding: '3rem 2rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            animation: 'slideUp 0.8s ease-out',
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(30px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {/* Error Code */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '4rem', md: '6rem' },
              fontWeight: 700,
              color: '#ff6b35',
              marginBottom: '1rem',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0)',
                },
                '40%': {
                  transform: 'translateY(-10px)',
                },
                '60%': {
                  transform: 'translateY(-5px)',
                },
              },
            }}
          >
            404
          </Typography>

          {/* Search Icon */}
          <Box
            sx={{
              margin: '2rem 0',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                border: '4px solid #ff6b35',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                color: '#ff6b35',
                animation: 'rotate 3s ease-in-out infinite',
                '@keyframes rotate': {
                  '0%, 100%': { transform: 'rotate(0deg) scale(1)' },
                  '50%': { transform: 'rotate(15deg) scale(1.1)' },
                },
              }}
            >
              🔍
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 600,
              color: '#495057',
              marginBottom: '1rem',
            }}
          >
            Page Not Found
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.1rem' },
              color: '#6c757d',
              lineHeight: 1.6,
              marginBottom: '2rem',
            }}
          >
            Oops! The page you're looking for doesn't exist. It might have been moved, deleted, or you entered the wrong URL.
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '2rem',
            }}
          >
            <Button
              variant="contained"
              onClick={handleGoHome}
              sx={{
                background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
                color: 'white',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: '8px',
                minWidth: '120px',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(255, 107, 53, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Return Home
            </Button>
            <Button
              variant="outlined"
              onClick={handleGoBack}
              sx={{
                color: '#6c757d',
                borderColor: '#6c757d',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: '8px',
                minWidth: '120px',
                '&:hover': {
                  backgroundColor: '#6c757d',
                  color: 'white',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Go Back
            </Button>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              marginTop: '2rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e9ecef',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.9rem',
                color: '#adb5bd',
                marginBottom: '1rem',
              }}
            >
              If you believe this is an error, please contact your system administrator.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                opacity: 0.6,
              }}
            >
              iCOM - Aptean
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFoundPage;